import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, ShieldCheck, XCircle } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import type { DetalleDelCobro } from '@/shared/api/tipos';
import { CampoDelPanel, PanelLateral, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { nombreDelEstadoDePago } from './estados';

export function PanelDeVerificacion({
  abierto,
  alCerrar,
}: {
  abierto: boolean;
  alCerrar: () => void;
}) {
  const [comprobante, setComprobante] = useState('');
  const [codigo, setCodigo] = useState('');
  const [falso, setFalso] = useState(false);

  const comprobar = useMutation({
    mutationFn: () =>
      api.get<DetalleDelCobro>(
        `/cobros/verificar?comprobante=${encodeURIComponent(comprobante.trim())}` +
          `&codigo=${encodeURIComponent(codigo.trim())}`,
      ),
    onSuccess: () => setFalso(false),
    onError: (causa) => {

      setFalso(causa instanceof ErrorDeApi && causa.status === 404);
    },
  });

  const listo = comprobante.trim().length > 0 && codigo.trim().length > 0;
  const d = comprobar.data;

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Verificar un recibo"
      descripcion="Compruebe que salió de este sistema"
      icono={ShieldCheck}
      alEnviar={(evento) => {
        evento.preventDefault();
        if (listo) {
          setFalso(false);
          comprobar.mutate();
        }
      }}
      enviando={!listo || comprobar.isPending}
    >
      <CampoDelPanel
        etiqueta="Número del recibo"
        requerido
        ayuda="El que sale grande en la cabecera del papel."
      >
        <input
          value={comprobante}
          onChange={(e) => {
            setComprobante(e.target.value);
            setFalso(false);
            comprobar.reset();
          }}
          placeholder="REC-912-20260831-00002"
          className={`${claseEntradaPanel} importe`}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Código de verificación"
        requerido
        ayuda="Ocho caracteres, al pie del recibo. Da igual mayúsculas o guion."
      >
        <input
          value={codigo}
          onChange={(e) => {
            setCodigo(e.target.value);
            setFalso(false);
            comprobar.reset();
          }}
          placeholder="A3F9-K2M7"
          maxLength={12}
          className={`${claseEntradaPanel} importe uppercase tracking-widest`}
        />
      </CampoDelPanel>

      {falso && (
        <p
          role="alert"
          className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-justify text-[11px] uppercase leading-relaxed text-red-900"
        >
          <XCircle className="mt-0.5 size-4 shrink-0" aria-hidden />
          <span>
            <span className="block text-sm font-bold">Ese recibo no consta</span>
            Compruebe que copió bien el número y el código. Si están bien copiados, ese papel
            no salió de este sistema.
          </span>
        </p>
      )}

      {d && (
        <div className="rounded-xl border border-marca-200 bg-marca-50 p-4">
          <p className="flex items-center gap-2 text-sm font-bold uppercase text-marca-800">
            <CheckCircle2 className="size-5 shrink-0" aria-hidden />
            Recibo auténtico
          </p>

          <dl className="mt-3 grid grid-cols-2 gap-3">
            <Dato rotulo="Quién pagó" valor={d.cliente} />
            <Dato rotulo="Identificación" valor={d.identificacion} />
            <Dato rotulo="Fecha" valor={enFecha(d.fechaPago)} />
            <Dato rotulo="Total" valor={`$${d.total.toFixed(2)}`} />
            <Dato rotulo="Atendió" valor={d.cajero} />
            <Dato rotulo="Caja" valor={d.nombreDeLaCaja ?? 'Serie general'} />
          </dl>

          {d.estado !== 'registrado' && (
            <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-[11px] uppercase leading-relaxed text-amber-900">
              El recibo es auténtico, pero este cobro está{' '}
              <b>{nombreDelEstadoDePago(d.estado).toLowerCase()}</b>. Compruébelo
              antes de darlo por bueno.
            </p>
          )}

          <p className="mt-3 text-justify text-[10px] uppercase leading-relaxed text-armazon-500">
            Compare estos datos con los del papel. Si el importe impreso no coincide con el
            de aquí, el papel fue retocado.
          </p>
        </div>
      )}
    </PanelLateral>
  );
}

function Dato({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
        {rotulo}
      </dt>
      <dd className="truncate text-xs font-medium uppercase text-armazon-800">{valor}</dd>
    </div>
  );
}

function enFecha(iso: string): string {
  const [anio, mes, dia] = iso.split('-');
  return `${dia}/${mes}/${anio}`;
}
